import { useState } from 'react'

import { Box, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { OneAlert, Textarea } from '@factorialco/f0-react/dist/experimental'
import { Pig } from '@factorialco/f0-react/icons/app'
import { ScheduleUpsellDiscountDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useModuleDiscount from 'modules/upsell/hooks/useModuleDiscount'

type SurveyStepProps = {
  onClose: () => void
  reopenModal: () => void
  moduleName: string
  moduleSlug: string
}

const SurveyStep = ({ onClose, reopenModal, moduleName, moduleSlug }: SurveyStepProps) => {
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [otherReason, setOtherReason] = useState<string>('')
  const [isDiscountAdded, setIsDiscountAdded] = useState<boolean>(false)
  const { discount, discountLoading, createDiscount, createDiscountLoading, refetchDiscounts } =
    useModuleDiscount({
      moduleName,
      moduleSlug,
    })
  const [scheduleUpsellDiscount, { loading: scheduleDiscountLoading }] = useMutation(
    ScheduleUpsellDiscountDocument,
    {
      extractorFn: (data) => data.upsellMutations.scheduleUpsellUpsellDiscount,
    }
  )
  const isLoading = createDiscountLoading || scheduleDiscountLoading

  const invalidModules = ['trainings', 'lms']

  const isInvalidModule = () => {
    return invalidModules.includes(moduleSlug)
  }

  const showDiscountDropdown = () => {
    if (isInvalidModule()) return false
    return discountLoading ? false : !discount?.percentage
  }

  const scheduleDiscount = () => {
    return scheduleUpsellDiscount({
      variables: {
        discountType: 'abandoned_cart',
        module: moduleName,
      },
    })
  }

  const surveyOptions = [
    {
      value: 'product_demo',
      label: i18n.t('self_upsell.survey_step.options.product_demo'),
      dropdown: false,
    },
    {
      value: 'too_expensive',
      label: i18n.t('self_upsell.survey_step.options.too_expensive'),
      // Only show dropdown if there is no discount already applied
      dropdown: showDiscountDropdown(),
    },
    {
      value: 'not_sure_if_it_fits_our_needs',
      label: i18n.t('self_upsell.survey_step.options.not_sure_if_it_fits_our_needs'),
      dropdown: false,
    },
    {
      value: 'just_wanted_to_explore',
      label: i18n.t('self_upsell.survey_step.options.just_wanted_to_explore'),
      dropdown: false,
    },
    {
      value: 'had_issues_completing_the_payment',
      label: i18n.t('self_upsell.survey_step.options.had_issues_completing_the_payment'),
      dropdown: false,
    },
    {
      value: 'other_reason',
      label: i18n.t('self_upsell.survey_step.options.other_reason'),
      dropdown: false,
    },
  ]

  const handleClose = async () => {
    if (!discount?.percentage && !isDiscountAdded && !isInvalidModule()) {
      await scheduleDiscount()
    }
    onClose()
  }

  const handleSubmit = () => {
    track(trackingEvents.upsell.submitSelfUpsellDissmisReason, {
      module: moduleName,
      reason: selectedReason,
      other_reason: otherReason,
    })
    app.setFlash({
      type: 'correct',
      message: i18n.t('self_upsell.survey_step.submit_success'),
    })

    handleClose()
  }

  const handleSkip = () => {
    track(trackingEvents.upsell.skipSelfUpsellDissmisReason, {
      module: moduleName,
    })

    handleClose()
  }

  const handleGetDiscount = async () => {
    track(trackingEvents.upsell.clickGetDiscountButton, {
      module: moduleName,
      source: 'checkout-exit-modal',
    })

    const result = await createDiscount({ isAutomatedDiscount: false })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      await refetchDiscounts()
      setIsDiscountAdded(true)
      app.setFlash({
        type: 'correct',
        message: i18n.t('self_upsell.survey_step.option_dropdown.success_message', {
          moduleName,
        }),
        time: 2000,
      })
      reopenModal()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('self_upsell.survey_step.option_dropdown.error_message'),
        time: 2000,
      })
      onClose()
    }
  }

  useEffect(() => {
    if (selectedReason === 'too_expensive' && !discount?.percentage) {
      track(trackingEvents.upsell.viewGetDiscountButton, {
        module: moduleName,
        source: 'checkout-exit-modal',
      })
    }
  }, [selectedReason])

  return (
    <F0Dialog
      isOpen
      onClose={handleSkip}
      title={i18n.t('self_upsell.survey_step.title')}
      primaryAction={{
        label: i18n.t('self_upsell.survey_step.submit'),
        onClick: handleSubmit,
        disabled: !selectedReason,
        loading: isLoading,
      }}
      secondaryAction={{
        label: i18n.t('self_upsell.survey_step.skip'),
        onClick: handleSkip,
        disabled: isLoading,
      }}
    >
      <Box gap="s32">
        <Box gap="s16">
          <Text.Normal size="200" color="grey800">
            {i18n.t('self_upsell.survey_step.description')}
          </Text.Normal>
          <Box gap="s8">
            {surveyOptions.map((option, index) => (
              <Box key={index} gap="s8">
                <Box gap="s16" flexDirection="row" alignItems="center">
                  <RadioButton
                    checked={selectedReason === option.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    value={option.value}
                    size="small"
                  />
                  <Text.Normal size="200" color="black">
                    {option.label}
                  </Text.Normal>
                </Box>
                {option.dropdown && selectedReason === option.value && !discount?.percentage && (
                  <OneAlert
                    title={i18n.t('self_upsell.survey_step.option_dropdown.title', {
                      discount: '20',
                      duration: '3',
                    })}
                    description={i18n.t('self_upsell.survey_step.option_dropdown.subtitle')}
                    icon={Pig}
                    variant="neutral"
                    action={{
                      label: i18n.t('self_upsell.survey_step.option_dropdown.button'),
                      onClick: handleGetDiscount,
                    }}
                  />
                )}
              </Box>
            ))}
            {selectedReason === 'other_reason' && (
              <Box gap="s16">
                <Textarea
                  label={i18n.t('self_upsell.survey_step.other_reason_label')}
                  value={otherReason}
                  onChange={(value) => setOtherReason(value)}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default SurveyStep
